import React from "react";
import { Dialog, Box, Typography, IconButton, Slide, useMediaQuery, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PersonIcon from "@mui/icons-material/Person";
import HistoryIcon from "@mui/icons-material/History";
import { TransitionProps } from "@mui/material/transitions";
import { useModalStyles } from "../../common/useModalStyles";

export interface ProductReturnTracking {
  productReturnId: number;
  parentReturnId: number | null;
  submittedBy: string | null;
  submittedAt: string | null;
  decisionStatus: string | null;
  decisionBy: string | null;
  decisionAt: string | null;
  returnPendingSubmittedBy: string | null;
  returnPendingTimeStamp: string | null;
  parentHistory: ProductReturnTracking[];
}

interface ReturnAuditPopupProps {
  open: boolean;
  onClose: () => void;
  data: ProductReturnTracking | null;
}

// Style tokens passed down to the history cards (so they match the modal theme).
interface CardStyles {
  cardBg: string;
  border: string;
  textPrimary: string;
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const AuditCard = ({
  icon,
  text,
  color,
  styles,
}: {
  icon: React.ReactNode;
  text: React.ReactNode;
  color: string;
  styles: CardStyles;
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.2,
      p: 1.8,
      mb: 1,
      borderRadius: "12px",
      backgroundColor: styles.cardBg,
      border: `1px solid ${styles.border}`,
    }}
  >
    <Box sx={{ color, display: "flex", alignItems: "center" }}>{icon}</Box>
    <Typography
      variant="body2"
      sx={{ color, display: "flex", alignItems: "center", flexWrap: "wrap", wordBreak: "break-word", fontWeight: 500 }}
    >
      {text}
    </Typography>
  </Box>
);

const formatAction = (action: string, by: string | null, at: string | null) => {
  if (!by || !at) {
    return <>Return {action}</>;
  }
  return (
    <>
      Request {action} by&nbsp;
      <strong>{by}</strong>
      &nbsp;at&nbsp;
      <strong>{at}</strong>
    </>
  );
};

const renderHistory = (dto: ProductReturnTracking, styles: CardStyles): JSX.Element => {
  const cards: JSX.Element[] = [];
  if (dto.parentHistory && dto.parentHistory.length > 0) {
    dto.parentHistory.forEach((parent) => cards.push(renderHistory(parent, styles)));
  }
  cards.push(
    <AuditCard
      key={`${dto.productReturnId}-initiated`}
      icon={<PersonIcon fontSize="small" />}
      text={formatAction("Initiated", dto.submittedBy, dto.submittedAt)}
      color={styles.textPrimary}
      styles={styles}
    />
  );
  // Pending happened if the backend returned the pending-event fields — show it
  // regardless of the final decisionStatus (it may now be Rejected/Returned).
  if (dto.returnPendingSubmittedBy || dto.returnPendingTimeStamp) {
    const pendingBy = dto.returnPendingSubmittedBy ?? dto.decisionBy;
    const pendingAt = dto.returnPendingTimeStamp ?? dto.decisionAt;
    cards.push(
      <AuditCard
        key={`${dto.productReturnId}-pending`}
        icon={<WarningAmberIcon fontSize="small" />}
        text={
          pendingBy && pendingAt ? (
            <>
              Marked as Pending by&nbsp;
              <strong>{pendingBy}</strong>
              &nbsp;at&nbsp;
              <strong>{pendingAt}</strong>
            </>
          ) : (
            <>Return Pending</>
          )
        }
        color="#FFAB00"
        styles={styles}
      />
    );
  }
  if (dto.decisionStatus === "RETURNED") {
    cards.push(
      <AuditCard
        key={`${dto.productReturnId}-approved`}
        icon={<CheckCircleIcon fontSize="small" />}
        text={formatAction("Approved", dto.decisionBy, dto.decisionAt)}
        color="#22C55E"
        styles={styles}
      />
    );
  }
  if (dto.decisionStatus === "RETURN_REJECTED") {
    cards.push(
      <AuditCard
        key={`${dto.productReturnId}-rejected`}
        icon={<ErrorOutlineIcon fontSize="small" />}
        text={formatAction("Rejected", dto.decisionBy, dto.decisionAt)}
        color="#ef4444"
        styles={styles}
      />
    );
  }
  return <Box key={dto.productReturnId}>{cards}</Box>;
};

const ReturnAuditPopup: React.FC<ReturnAuditPopupProps> = ({
  open,
  onClose,
  data,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { tokens, paperSx, headerSx, iconBoxSx, bodySx, closeIconSx } = useModalStyles();

  if (!data) return null;

  const isRejected = data.decisionStatus === "RETURN_REJECTED";
  const cardStyles: CardStyles = {
    cardBg: tokens.cardBg,
    border: tokens.border,
    textPrimary: tokens.textPrimary,
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      TransitionComponent={Transition}
      PaperProps={{ sx: paperSx(isMobile) }}
    >
      <Box sx={headerSx}>
        <Box sx={iconBoxSx(tokens.accent)}>
          <HistoryIcon sx={{ color: tokens.accent, fontSize: "1.4rem" }} />
        </Box>
        <Box>
          <Typography fontWeight={700} sx={{ fontSize: "1.1rem", color: tokens.textPrimary, lineHeight: 1.3 }}>
            Return History
          </Typography>
          <Typography sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}>
            Return request activity timeline
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={closeIconSx}>
          <CloseIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
      </Box>

      <Box sx={bodySx}>
        {renderHistory(data, cardStyles)}
        {isRejected && (
          <Typography variant="body2" sx={{ color: "#FFAB00", mt: 1 }}>
            <strong>Note :</strong> Please contact <strong>Admin</strong> to initiate a Re-Return.
          </Typography>
        )}
      </Box>
    </Dialog>
  );
};

export default ReturnAuditPopup;
